import React, { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch, type Control, type FieldValues } from 'react-hook-form';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { FormSharedUserInput } from 'src/components/common/forms/FormSharedUserInput';
import { REGIONS, type Region } from 'src/constants';
import type { BusinessUnitDto, Country } from 'src/store/generated/leela-api-generated';
import {
  usePlatformAdminControllerCreateBusinessUnitMutation,
  usePlatformAdminControllerUpdateBusinessUnitMutation,
} from 'src/store/leela-api';

interface BuFormModalProps {
  bu?: BusinessUnitDto;
  open: boolean;
  onClose: () => void;
}

interface BuFormData {
  name: string;
  admins: string[];
  regions: string[];
  countries: string[];
}

const SELECT_ALL = '__SELECT_ALL__';

const getCountriesForRegions = (regions: readonly string[]): string[] => REGIONS
  .filter(region => regions.includes(region.name))
  .flatMap(region => [...region.countries]);

export const BuFormModal: React.FC<BuFormModalProps> = ({ bu, open, onClose }) => {
  const { t } = useTranslation();
  const { createToast } = useToasterContext();
  const [createBu, { isLoading: isCreating }] = usePlatformAdminControllerCreateBusinessUnitMutation();
  const [updateBu, { isLoading: isUpdating }] = usePlatformAdminControllerUpdateBusinessUnitMutation();
  const isEditing = !!bu;
  const isSubmitting = isCreating || isUpdating;

  const { control, handleSubmit, reset, setValue } = useForm<BuFormData>({
    defaultValues: { name: '', admins: [], regions: [], countries: [] },
  });

  const [nameValue, setNameValue] = useState('');
  const selectedRegions = useWatch({ control, name: 'regions' }) as string[];
  const selectedCountries = useWatch({ control, name: 'countries' }) as string[];

  // Pre-fill on edit
  useEffect(() => {
    if (bu) {
      const buRegions: string[] = Array.from(new Set(
        (bu.countries ?? [])
          .map(c => REGIONS.find(r => r.countries.includes(c as Country))?.name)
          .filter((r): r is string => !!r),
      ));
      const name = bu.name ?? '';
      reset({ name, admins: bu.admins ?? [], regions: buRegions, countries: bu.countries ?? [] });
      setNameValue(name);
    } else {
      reset({ name: '', admins: [], regions: [], countries: [] });
      setNameValue('');
    }
  }, [bu, open, reset]);

  // Region → country cascade
  const availableCountries = useMemo(
    (): string[] => getCountriesForRegions(selectedRegions ?? []),
    [selectedRegions],
  );

  const DESELECT_ALL = '__DESELECT_ALL__';

  const allRegionsSelected = selectedRegions?.length === REGIONS.length;
  const allCountriesSelected = selectedCountries?.length === availableCountries.length && availableCountries.length > 0;

  const handleRegionChange = (value: string | string[]) => {
    const values = Array.isArray(value) ? value : [value];
    const nextRegions = values.includes(DESELECT_ALL)
      ? []
      : values.includes(SELECT_ALL)
        ? REGIONS.map(region => region.name)
        : values;
    const currentRegions = selectedRegions ?? [];
    const retainedRegions = currentRegions.filter(region => nextRegions.includes(region));
    const retainedCountrySet = new Set(getCountriesForRegions(retainedRegions));
    const retainedCountries = (selectedCountries ?? []).filter(country => retainedCountrySet.has(country));
    const addedRegions = nextRegions.filter(region => !currentRegions.includes(region));
    const addedCountries = getCountriesForRegions(addedRegions);

    setValue('regions', nextRegions);
    setValue('countries', Array.from(new Set([...retainedCountries, ...addedCountries])));
  };

  const handleCountryChange = (value: string | string[]) => {
    const values = Array.isArray(value) ? value : [value];
    if (values.includes(DESELECT_ALL)) {
      setValue('countries', []);
    } else if (values.includes(SELECT_ALL)) {
      setValue('countries', [...availableCountries]);
    } else {
      setValue('countries', values);
    }
  };

  const onSubmit = async (data: BuFormData) => {
    const name = data.name.trim();
    if (!name || data.countries.length === 0) return;
    try {
      if (isEditing && bu) {
        await updateBu({ id: bu.id, updateBusinessUnitDto: { name, admins: data.admins, countries: data.countries as Country[] } }).unwrap();
        createToast({ type: AlertType.SUCCESS, message: t('bu_management_update_success') });
      } else {
        await createBu({ createBusinessUnitDto: { name, admins: data.admins, countries: data.countries as Country[] } }).unwrap();
        createToast({ type: AlertType.SUCCESS, message: t('bu_management_create_success') });
      }
      onClose();
    } catch (error: unknown) {
      const message = (error as { data?: { message?: string } })?.data?.message;
      if (message?.includes('already exists')) {
        createToast({ type: AlertType.ERROR, message: t('bu_management_duplicate_name') });
      } else {
        createToast({ type: AlertType.ERROR, message: isEditing ? t('bu_management_update_error') : t('bu_management_create_error') });
      }
    }
  };

  return (
    <Modal
      data-testid="bu-form-modal"
      open={open}
      scrollContainer="modal"
      title={isEditing ? t('bu_management_edit') : t('bu_management_create')}
      width={700}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Column spacing="400">
          <Column spacing="100">
            <Text type="b200">{t('bu_management_name')}</Text>
            <Input
              data-testid="bu-form-name-input"
              placeholder={t('bu_management_name_placeholder')}
              value={nameValue}
              onChange={(value: string) => { setNameValue(value); setValue('name', value); }}
            />
          </Column>

          <Column spacing="100">
            <Text type="b200">{t('bu_management_admins')}</Text>
            <FormSharedUserInput
              control={control as unknown as Control<FieldValues>}
              label=""
              name="admins"
              requireEmail
            />
          </Column>

          <Column spacing="100">
            <Text type="b200">{t('bu_management_regions')}</Text>
            <Select
              data-testid="bu-form-region-select"
              placeholder={t('bu_management_regions_placeholder')}
              value={selectedRegions ?? []}
              onChange={handleRegionChange}
            >
              <SelectOption
                label={allRegionsSelected ? t('bu_management_deselect_all') : t('bu_management_select_all')}
                value={allRegionsSelected ? DESELECT_ALL : SELECT_ALL}
              />
              {REGIONS.map((region: Region) => (
                <SelectOption key={region.name} label={region.name} value={region.name} />
              ))}
            </Select>
          </Column>

          <Column spacing="100">
            <Text type="b200">{t('bu_management_countries')}</Text>
            <Select
              data-testid="bu-form-country-select"
              disabled={!selectedRegions?.length}
              placeholder={t('bu_management_countries_placeholder')}
              value={selectedCountries ?? []}
              onChange={handleCountryChange}
            >
              <SelectOption
                label={allCountriesSelected ? t('bu_management_deselect_all') : t('bu_management_select_all')}
                value={allCountriesSelected ? DESELECT_ALL : SELECT_ALL}
              />
              {availableCountries.map((country: string) => (
                <SelectOption key={country} label={country} value={country} />
              ))}
            </Select>
          </Column>
        </Column>

        <Column spacingInset="500 000 000 000">
          <ModalFooter>
            <Row alignmentHorizontal="end" spacing="300">
              <Button data-testid="bu-form-cancel-button" type="secondary" onClick={onClose}>
                {t('bu_management_cancel')}
              </Button>
              <Button
                data-testid="bu-form-save-button"
                disabled={isSubmitting || !nameValue.trim() || selectedCountries?.length === 0}
                submit
                type="primary"
              >
                {t('bu_management_save')}
              </Button>
            </Row>
          </ModalFooter>
        </Column>
      </form>
    </Modal>
  );
};
